/**
 * EntityRegistry - Spatial indexing and lifecycle management for all game entities.
 */

import { EntityState, EntityCategory } from './Types';

export class EntityRegistry {
  private entities: Map<string, EntityState> = new Map();
  private corpsesToClean: Array<{ id: string; despawnTime: number }> = [];
  private remoteProvider?: () => EntityState[];

  public setRemoteProvider(provider: () => EntityState[]): void {
    this.remoteProvider = provider;
  }

  public register(entity: EntityState): void {
    this.entities.set(entity.id, entity);
  }

  public unregister(id: string): void {
    this.entities.delete(id);
  }

  public get(id: string): EntityState | undefined {
    const local = this.entities.get(id);
    if (local) return local;
    if (this.remoteProvider) {
      const remotes = this.remoteProvider();
      return remotes.find((r) => r.id === id);
    }
    return undefined;
  }

  public getAll(): EntityState[] {
    const base = Array.from(this.entities.values());
    if (this.remoteProvider) {
      const remotes = this.remoteProvider();
      if (remotes && remotes.length > 0) {
        return [...base, ...remotes];
      }
    }
    return base;
  }

  public getByCategory(category: EntityCategory): EntityState[] {
    return this.getAll().filter((e) => e.category === category && !e.isDead);
  }

  public getParty(): EntityState[] {
    return this.getAll().filter(
      (e) => (e.category === 'player' || e.category === 'companion') && !e.isDead
    );
  }

  public getMonsters(): EntityState[] {
    return this.getAll().filter((e) => e.category === 'monster' && !e.isDead);
  }

  public getResources(): EntityState[] {
    return this.getAll().filter((e) => e.category === 'resource');
  }

  public getPlayer(): EntityState | undefined {
    return this.getAll().find((e) => e.category === 'player');
  }

  public getDistance(a: { x: number; y: number }, b: { x: number; y: number }): number {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  public getNearestAliveMonster(origin: { x: number; y: number }, maxRange = 15): EntityState | null {
    let nearest: EntityState | null = null;
    let minDist = maxRange;
    for (const m of this.getMonsters()) {
      const d = this.getDistance(origin, m);
      if (d < minDist) {
        minDist = d;
        nearest = m;
      }
    }
    return nearest;
  }

  public getNearestInjuredAlly(origin: { x: number; y: number }, maxRange = 12): EntityState | null {
    let mostInjured: EntityState | null = null;
    let lowestPct = 0.95; // Only target if HP < 95%
    for (const ally of this.getParty()) {
      const d = this.getDistance(origin, ally);
      if (d <= maxRange) {
        const pct = ally.resources.hp / ally.resources.maxHp;
        if (pct < lowestPct) {
          lowestPct = pct;
          mostInjured = ally;
        }
      }
    }
    return mostInjured;
  }

  public markForDespawn(id: string, delaySeconds = 4.0): void {
    this.corpsesToClean.push({
      id,
      despawnTime: performance.now() + delaySeconds * 1000,
    });
  }

  public updateDespawns(currentTime: number): void {
    if (this.corpsesToClean.length === 0) return;
    this.corpsesToClean = this.corpsesToClean.filter((entry) => {
      if (currentTime >= entry.despawnTime) {
        this.unregister(entry.id);
        return false;
      }
      return true;
    });
  }

  public clear(): void {
    this.entities.clear();
    this.corpsesToClean = [];
  }
}
